import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { PublicClientApplication, Configuration, BrowserCacheLocation, InteractionRequiredAuthError } from '@azure/msal-browser';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AuthOpenIdService {
  private router = inject(Router);

  private readonly TENANT_ID = environment.tenantId;
  private readonly CLIENT_ID = environment.clientId;
  private readonly SCOPES = environment.scopes.split(' ');
  private readonly REDIRECT_URI = window.location.origin + '/auth/callback';

  private msalConfig: Configuration = {
    auth: {
      clientId: this.CLIENT_ID,
      authority: `https://login.microsoftonline.com/${this.TENANT_ID}`,
      redirectUri: this.REDIRECT_URI,
      postLogoutRedirectUri: window.location.origin + '/auth/login'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    }
  };

  private msalInstance = new PublicClientApplication(this.msalConfig);
  private accessToken: string | null = null;
  private _isReady = signal(false);
  public readonly isReady = this._isReady.asReadonly();

  constructor() {
    this.initializeMsal();
  }

  private async initializeMsal() {
    try {
      await this.msalInstance.initialize();
      
      // Handle redirect callback
      await this.msalInstance.handleRedirectPromise();

      // Check if user is already logged in
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(accounts[0]);
        const tokenResponse = await this.acquireTokenSilently();
        if (tokenResponse?.accessToken) {
          this.accessToken = tokenResponse.accessToken;
        }
      }
    } catch (error) {
      console.error('MSAL initialization failed:', error);
    } finally {
      this._isReady.set(true);
    }
  }

  /** Wacht tot MSAL geladen is. Veilig om meerdere keren aan te roepen. */
  waitUntilReady(): Promise<void> {
    if (this._isReady()) return Promise.resolve();
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (this._isReady()) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  private async acquireTokenSilently() {
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length === 0) return null;

    const account = accounts[0];
    const request = {
      account,
      scopes: this.SCOPES,
      redirectUri: this.REDIRECT_URI
    };

    try {
      return await this.msalInstance.acquireTokenSilent(request);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.log('Silent token acquisition failed, interactive login required');
        return null;
      }
      console.error('Token acquisition error:', error);
      return null;
    }
  }

  async login(): Promise<boolean> {
    try {
      const request = {
        scopes: this.SCOPES,
        redirectUri: this.REDIRECT_URI
      };

      // Redirect to Microsoft login (no popup! PWA-friendly)
      await this.msalInstance.loginRedirect(request);
      return true; // Will redirect, so this won't actually return
    } catch (error) {
      console.error('Login failed:', error);
      return false;
    }
  }

  async logout() {
    try {
      // Clear active account
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(null);
      }
      
      // Logout - this will redirect to Microsoft logout endpoint
      await this.msalInstance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin + '/auth/login'
      });
      
      this.accessToken = null;
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  isLoggedIn(): boolean {
    return this.msalInstance.getAllAccounts().length > 0 && this.accessToken !== null;
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.accessToken) {
      const tokenResponse = await this.acquireTokenSilently();
      if (tokenResponse?.accessToken) {
        this.accessToken = tokenResponse.accessToken;
      }
    }
    return this.accessToken;
  }

  getAccessTokenSync(): string | null {
    // Returns cached token synchronously for use in interceptors
    return this.accessToken;
  }

  async refreshToken(): Promise<boolean> {
    try {
      const tokenResponse = await this.acquireTokenSilently();
      if (tokenResponse?.accessToken) {
        this.accessToken = tokenResponse.accessToken;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }
}
